import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import { customerSelectorStyles } from "@insite/content-library/Components/CustomerSelector";
import { orderUploadStyles } from "@insite/content-library/Components/OrderUpload";
import { productDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { productPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { scheduleReminderModalStyles } from "@insite/content-library/Components/ScheduleReminderModal";
import { shareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { shareListModalStyles } from "@insite/content-library/Components/ShareListModal";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { manageSubscriptionsStyles } from "@insite/content-library/Widgets/AccountSettings/AccountSettingsManageSubscriptions";
import { addressBookStyles } from "@insite/content-library/Widgets/Addresses/AddressBook";
import { createAddressButtonStyles } from "@insite/content-library/Widgets/Addresses/CreateAddressButton";
import { currentBillingAddressStyles } from "@insite/content-library/Widgets/Addresses/CurrentBillingAddress";
import { currentShippingAddressStyles } from "@insite/content-library/Widgets/Addresses/CurrentShippingAddress";
import { assignBudgetsStyles } from "@insite/content-library/Widgets/Budget/AssignBudgets";
import { enforcementLevelDisplayStyles } from "@insite/content-library/Widgets/Budget/EnforcementLevelDisplay";
import { reviewBudgetStyles } from "@insite/content-library/Widgets/Budget/ReviewBudget";
import { changePasswordStyles } from "@insite/content-library/Widgets/ChangePassword/ChangePassword";
import { checkoutShippingCarrierServiceStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitCarrierService";
import { pageContainerStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPageContainer";
import { checkoutReviewAndSubmitPaymentDetailsStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPaymentDetails";
import { checkoutShippingOrderNotesEntryStyles } from "@insite/content-library/Widgets/CheckoutShipping/CheckoutShippingOrderNotesEntry";
import { recentWishListsStyles } from "@insite/content-library/Widgets/Common/RecentWishLists";
import { cardStyles } from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsLineCard";
import { tableStyles } from "@insite/content-library/Widgets/InvoiceHistory/InvoiceHistoryTable";
import { paginationStyles } from "@insite/content-library/Widgets/MyLists/MyListsPagination";
import { productListLineStyles } from "@insite/content-library/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { orderApprovalDetailsActionButtonsStyles } from "@insite/content-library/Widgets/OrderApprovalDetails/OrderApprovalDetailsActionButtons";
import { orderConfirmationBillingInformationStyles } from "@insite/content-library/Widgets/OrderApprovalDetails/OrderApprovalDetailsBillingInformation";
import { filterStyles } from "@insite/content-library/Widgets/OrderApprovalList/OrderApprovalListFilter";
import { dateRangeStyles } from "@insite/content-library/Widgets/OrderHistory/OrderHistorySearchFieldDateRange";
import { orderHistoryTableStyles } from "@insite/content-library/Widgets/OrderHistory/OrderHistoryTable";
import { filtersAccordionStyles } from "@insite/content-library/Widgets/ProductList/ProductListFiltersAccordion";
import { actionsStyles } from "@insite/content-library/Widgets/ProductList/ProductListTable";
import { requisitionsProductListStyles } from "@insite/content-library/Widgets/Requisitions/RequisitionsProductList";
import { requisitionsProductListLineStyles } from "@insite/content-library/Widgets/Requisitions/RequisitionsProductListLine";
import { rfqConfirmationHeaderStyles } from "@insite/content-library/Widgets/RfqConfirmation/RfqConfirmationHeader";
import { rfqMyQuotesFilterStyles } from "@insite/content-library/Widgets/RfqMyQuotes/RfqMyQuotesFilter";
import { rfqMyQuotesTableStyles } from "@insite/content-library/Widgets/RfqMyQuotes/RfqMyQuotesTable";
import { rfqQuoteDetailsInformationStyles } from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsInformation";
import { rfqRequestQuoteProductListStyles } from "@insite/content-library/Widgets/RfqRequestQuote/RfqRequestQuoteProductList";
import { savedOrderDetailsActionButtonsStyles } from "@insite/content-library/Widgets/SavedOrderDetails/SavedOrderDetailsActionButtons";
import { userListHeaderStyles } from "@insite/content-library/Widgets/UserList/UserListHeader";
import { userListUsersTableStyles } from "@insite/content-library/Widgets/UserList/UserListUsersTable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import KeepMeLoginModal from "@theHescoBlueprint/Widgets/KeepMeLoginModal";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

rfqConfirmationHeaderStyles.buttonGridItem = {
    css: css`
        justify-content: flex-end;
    `,
    width: [2, 2, 2, 7, 6],
};

rfqConfirmationHeaderStyles.titleGridItem = {
    width: [10, 10, 10, 5, 6],
};

rfqRequestQuoteProductListStyles.lineCountWrapper = {
    css: css`
        border-bottom: 0px solid ${getColor("common.border")};
        padding-bottom: 2px;
    `,
};

orderConfirmationBillingInformationStyles.billingInformationBillingAddressGridItem = {
    width: 12,
    printWidth: 4,
    css: css`
        flex-direction: column;
    `,
};

addressBookStyles.addressCard = {
    gridContainer: { gap: 20 },
    addressInfoDisplayGridItem: { width: [10, 10, 5, 7, 7] },
    actionsGridItem: {
        width: [2, 2, 7, 5, 5],
        css: css`
            justify-content: flex-end;
        `,
    },
    actionUseAsShippingAddressButton: { color: "secondary" },
    actionEditLink: {
        css: css`
            display: none;
            margin: 20px auto 0 auto;
        `,
    },
    bottomBorderGridItem: { width: 12 },
    bottomBorder: {
        css: css`
            height: 1px;
            background-color: ${getColor("common.border")};
            width: 100%;
        `,
    },
    overflowMenu: {
        cssOverrides: {
            menu: css`
                width: 210px;
            `,
        },
    },
};

currentShippingAddressStyles.editLinkGridItem = {
    css: css`
        display: none;
    `,
};

currentBillingAddressStyles.editLinkGridItem = {
    css: css`
        display: none;
    `,
};

checkoutShippingCarrierServiceStyles.deliveryDateGridItem = {
    width: [12, 12, 12, 6, 6],
    css: css`
        > div {
            width: 100%;
            > div[class^="FormFieldStyle"] {
                padding-right: 0;
                > div[class^="FormInputWrapper"] {
                    > div[class^="DescriptionWrapper"] {
                        span {
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        null,
                                        null,
                                        null,
                                        css`
                                            line-height: 20px;
                                        `,
                                        null,
                                    ],
                                    "max",
                                )}
                        }
                    }
                }
            }
        }
    `,
};

checkoutShippingOrderNotesEntryStyles.textArea = {
    cssOverrides: {
        inputSelect: css`
            max-width: 100%;
        `,
    },
};
orderApprovalDetailsActionButtonsStyles.printButton = {
    variant: "secondary",
    buttonType: "outline",
};

enforcementLevelDisplayStyles.valueText = {
    css: css`
        margin-top: 3px;
        color: ${getColor("primary")};
    `,
};

enforcementLevelDisplayStyles.labelText = {
    as: "h3",
    variant: "h6",
    css: css`
        display: inline-block;
        margin-bottom: 0;
    `,
};

pageContainerStyles.hiddenShippingInfoAndProductListWide = {
    below: "lg",
    css: css`
        width: 100%;
    `,
};

currentBillingAddressStyles.editLink = {
    css: css`
        margin-top: 3px;
    `,
};

currentShippingAddressStyles.editLink = {
    css: css`
        margin-top: 3px;
    `,
};

actionsStyles.productImageStyles = {
    compareCheckbox: {
        css: css`
            align-self: start;
            padding-top: 15px;
            cursor: pointer;
        `,
    },
};

actionsStyles.leftColumnGridItem = {
    width: [4, 4, 4, 3, 3],
    css: css`
        padding-right: 15px;
        align-items: center;
    `,
};

customerSelectorStyles.pagination = {
    cssOverrides: {
        pagination: css`
            padding-top: 15px;
        `,
    },
};

manageSubscriptionsStyles.manageSubscriptionsCheckbox = {
    typographyProps: {
        css: css`
            margin-left: 10px;
            background-image: none !important;
        `,
    },
};

productDescriptionStyles.productDetailLink = {
    css: css`
        width: 100%;
        span {
            color: #333 !important;
            font-weight: bold;
        }
        &:hover {
            span {
                color: #333 !important;
            }
        }
    `,
    typographyProps: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
};

savedOrderDetailsActionButtonsStyles.printButton = {
    variant: "secondary",
    buttonType: "outline",
};

reviewBudgetStyles.filterWrapper = {
    css: css`
        display: inline-block;
        margin: 25px 0 15px 0;
        width: 100%;
    `,
};

reviewBudgetStyles.wrapper = {
    css: css`
        display: inline-block;
        margin: 0 30px 15px 0;
        min-width: 15%;
        ${({ theme }) => {
            return breakpointMediaQueries(theme, [
                css`
                    min-width: 90%;
                `,
                css`
                    min-width: 95%;
                `,
                css`
                    min-width: 39%;
                `,
                css`
                    min-width: 27.5%;
                `,
                css`
                    min-width: 15%;
                `,
            ]);
        }}
    `,
};

userListUsersTableStyles.container = {
    css: css`
        overflow-x: auto;
        margin-bottom: 20px;
        position: relative;
    `,
};

userListHeaderStyles.searchGridItem = {
    width: [12, 12, 6, 6, 6],
};

userListHeaderStyles.wrapper = {
    css: css`
        margin-bottom: 1rem;
        h1[data-test-selector="PageTitle"] {
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 26px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        }
    `,
};

scheduleReminderModalStyles.scheduleButton = {
    variant: "primary",
    css: css`
        white-space: nowrap;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        width: 100%;
                    `,
                ],
                "max",
            )}
    `,
};

scheduleReminderModalStyles.scheduleButtonGridItem = {
    width: [12, 3, 2, 2, 2],
    css: css`
        justify-content: flex-end;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-left: 20px !important;
                    `,
                    css`
                        margin-left: 20px !important;
                    `,
                ],
                "minMax",
            )}
    `,
};

scheduleReminderModalStyles.cancelButton = {
    variant: "secondary",
    css: css`
        white-space: nowrap;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        width: 100%;
                    `,
                ],
                "max",
            )}
    `,
};

orderHistoryTableStyles.container = {
    css: css`
        overflow: auto;
        margin-bottom: 20px;
        position: relative;
    `,
};

tableStyles.container = {
    css: css`
        overflow: auto;
        margin-bottom: 20px;
        position: relative;
    `,
};

tableStyles.shipToCell = {
    css: css`
        span {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            display: block;
            max-width: 300px;
        }
    `,
};

rfqMyQuotesTableStyles.wrapper = {
    css: css`
        overflow: auto;
        margin-bottom: 20px;
        position: relative;
    `,
};

shareEntityButtonStyles.clickable = {
    css: css`
        border-color: ${getColor("primary")} !important;
        &:hover {
            background: #f3f3f3 !important;
        }
    `,
};

orderUploadStyles.uploadFileButtonLabel = {
    css: css`
        font-size: 15px;
        font-weight: bold;
        text-transform: uppercase;
    `,
};

productListLineStyles.deleteLink = {
    typographyProps: { size: 15 },
    css: css`
        margin-top: 10px;
    `,
};

productListLineStyles.descriptionGridItem = {
    width: [8, 8, 4, 4, 4],
    printWidth: 6,
    css: css`
        flex-direction: column;
        padding-left: 10px;
        a[data-test-selector="productDescriptionLink"] {
            font-size: 16px;
            font-weight: 600;
            color: #333;
            span {
                color: #333;
            }
        }
    `,
};

productListLineStyles.partNumbersGridItem = {
    width: 12,
    css: css`
        padding: 0px;
        div[class^="GridItemStyle"] {
            flex-direction: row;
            align-items: center;
            legend {
                padding-left: 0;
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                padding-left: 5px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    );
                }}
            }
        }
    `,
};

productListLineStyles.addToCartButton = {
    css: css`
        ${({ theme }) => {
            return breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        width: 100%;
                        padding: 10px 0;
                        span {
                            font-size: 13px;
                        }
                    `,
                    null,
                    null,
                ],
                "max",
            );
        }}
    `,
};

productListLineStyles.priceGridItem = {
    width: [12, 12, 12, 12, 5],
    printWidth: 5,
    css: css`
        flex-direction: column;
        padding: 0 0 10px 5px;
        section {
            padding: 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-top: 10px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            p {
                margin: 0;
            }
        }
    `,
};

cardStyles.productNameLink = {
    css: css`
        font-size: 16px;
        font-weight: 600;
        span {
            color: #333 !important;
        }
    `,
};

cardStyles.totalItem = {
    width: [12, 12, 12, 12, 4],
    printWidth: 4,
    css: css`
        font-weight: 600;
        font-size: 18px;
        color: ${getColor("primary")};
    `,
};

cardStyles.priceItem = {
    width: [6, 6, 12, 12, 5],
    printWidth: 5,
    css: css`
        font-weight: 600;
    `,
};

paginationStyles.wrapper = {
    css: css`
        padding: 20px 0;
        ${({ theme }) => {
            return breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        span[class^="PerPageSelect"],
                        nav[role="navigation"] {
                            width: 100%;
                            justify-content: center;
                        }
                    `,
                    null,
                    null,
                    null,
                ],
                "max",
            );
        }}
    `,
};

changePasswordStyles.actions = {
    css: css`
        width: 100%;
        div[class^="GridOffset"] {
            max-width: 100%;
            margin: 0;
        }
    `,
};

createAddressButtonStyles.createNewAddressButtonGridItem = {
    width: 12,
    css: css`
        justify-content: flex-end;
        white-space: nowrap;
    `,
};

createAddressButtonStyles.overflowMenu = {
    cssOverrides: {
        wrapper: css`
            right: -22px;
        `,
    },
};

dateRangeStyles.datePickersWrapper = {
    css: css`
        display: flex;
        > div {
            width: 50%;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            &:nth-child(2) {
                > div[class^="FormFieldStyle"] {
                    padding: 0px 0 0px 10px;
                }
            }
        }
    `,
};

filterStyles.orderTotalAmountTextField = {
    cssOverrides: {
        formInputWrapper: css`
            margin-top: 10px;
        `,
    },
};

filterStyles.fromGridItem = {
    width: 6,
    css: css`
        > div {
            width: 100%;
        }
    `,
};
filterStyles.toGridItem = {
    width: 6,
    css: css`
        > div {
            width: 100%;
        }
    `,
};

shareListModalStyles.sendButton = {
    css: css`
        width: 100%;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    margin-bottom: 40px;
                `,
                css`
                    margin-bottom: 40px;
                `,
                null,
                null,
                null,
            ])};
    `,
};

shareListModalStyles.cancelButton = {
    variant: "secondary",
    css: css`
        width: 100%;
        ${({ theme }) => {
            return breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        padding: 0 2px;
                    `,
                ],
                "minMax",
            );
        }}
    `,
};
checkoutReviewAndSubmitPaymentDetailsStyles.paymentMethodGridItem = {
    width: 6,
    css: css`
        flex-direction: column;
        div[class^="DescriptionWrapper"] {
            span {
                line-height: 18px;
            }
        }
        [data-test-selector="checkoutReviewAndSubmit_paymentMethod"] {
            padding-right: 40px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    `,
};
requisitionsProductListStyles.linesWrapper = {
    css: css`
        margin-top: 20px;
        [data-test-selector="productPrice_unitNetPrice"] {
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 16px;
                        `,
                    ],
                    "minMax",
                )}
        }
        [data-test-selector="productPrice_unitOfMeasureLabel"] {
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: 13px;
                        `,
                    ],
                    "minMax",
                )}
        }
    `,
};
filterStyles.toDate = {};
filterStyles.fromDate = {};
dateRangeStyles.toDate = {};
dateRangeStyles.fromDate = {};
rfqMyQuotesFilterStyles.toDate = {};
rfqMyQuotesFilterStyles.fromDate = {};
rfqMyQuotesFilterStyles.expireToDate = {};
rfqMyQuotesFilterStyles.expireFromDate = {};
rfqQuoteDetailsInformationStyles.expirationDateDatePicker = {};

export interface HeaderStyles {
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerMain?: InjectableCss;
    headerMainWrapper?: InjectableCss;
    headerMainFixed?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    headerMain: {
        css: css`
            z-index: 99;
            padding: 0 !important;
            border-bottom: ${getColor("primary")} 5px solid;
            line-height: 24px;
            background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 37%,
                rgba(24, 36, 61, 1) 39%,
                rgba(24, 36, 61, 1) 100%
            );
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            background: #18243d;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            .logoSection {
                a[class^="StyledButton"] {
                    transition: all 0.3s ease;
                    img {
                        transition: all 0.3s ease;
                    }
                    .logo {
                        transition: all 0.3s ease;
                    }
                }
            }
            & + div[class^="PageStyle"] {
                nav[data-test-selector="pageBreadcrumbs"] {
                    width: 100%;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                null,
                                css`
                                    padding-bottom: 10px;
                                    overflow-x: auto;
                                    white-space: nowrap;
                                `,
                            ],
                            "max",
                        )}
                    span[aria-current="page"] {
                        color: #2e64b0;
                        font-weight: 600;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        -webkit-text-size-adjust: none;
                                        text-size-adjust: none;
                                    `,
                                    null,
                                    null,
                                ],
                                "minMax",
                            )}
                    }
                }
            }
        `,
    },
    headerMainFixed: {
        css: css`
            z-index: 99;
            padding: 0 !important;
            border-bottom: ${getColor("primary")} 5px solid;
            line-height: 22px;
            background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 37%,
                rgba(24, 36, 61, 1) 39%,
                rgba(24, 36, 61, 1) 100%
            );
            .logoSection {
                a[class^="StyledButton"] {
                    height: 45px;
                    width: 110px;
                    align-items: center;
                    justify-content: center;
                    margin-top: -3px;
                    transition: all 0.3s ease;
                    span[class^="TypographyStyle"] {
                        display: inline-flex;
                        transition: all 0.3s ease;
                    }
                    img:first-child{
                        display:none;
                    }
                    .stickyLogo {
                        height: 45px;
                        justify-content: flex-start;
                        display: inline-flex;
                        transition: all 0.3s ease;
                        img {
                            width: auto;
                            height: 45px;
                            transition: all 0.3s ease;
                        }
                    }
                    .logo {
                        display: none;
                        transition: all 0.3s ease;
                    }
                }
            }
            .searchSection {
                ${({ theme }) => {
                    return breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 10px;
                            `,
                            css`
                                padding: 0px;
                            `,
                            css`
                                padding: 0 15px 0 0;
                            `,
                        ],
                        "min",
                    );
                }}
                .welcomeGuest,
                span[data-test-selector="cartLabel"] {
                    display: none;
                }
                a[data-test-selector="cartLink"] {
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    top: 10px;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        );
                    }}
                }
                button[data-test-selector="expandMobileMenu"] {
                    top: 0;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            background: #fff;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    headerMainWrapper: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 50px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

productPriceStyles.price = {
    priceText: {
        weight: "bold",
        color: "primary",
        size: "20px",
        css: css`
            line-height: 18px;
            & + [data-test-selector="productPrice_unitOfMeasureLabel"] {
                font-size: 16px;
                white-space: nowrap;
            }
        `,
    },
};
filtersAccordionStyles.accordion = {
    css: css`
        padding-top: 20px;
        label {
            word-break: break-word;
        }
    `,
};
recentWishListsStyles.wishListCardGridItem = {
    width: 12,
    css: css`
        padding: 0;
        button {
            white-space: normal;
        }
    `,
};

requisitionsProductListLineStyles.priceGridItem = {
    width: [12, 12, 2, 2, 2],
    css: css`
        > section {
            padding-top: 0;
        }
    `,
};

requisitionsProductListLineStyles.descriptionGridItem = {
    width: [12, 12, 5, 6, 6],
    printWidth: 6,
    css: css`
        flex-direction: column;
        > a,
        > span {
            padding: 5px 0;
        }
        > div:last-child {
            > div[class^="GridWrapper"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                max-width: 100%;
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        }
    `,
};
requisitionsProductListLineStyles.partNumbersGridItem = {
    width: 12,
    css: css`
        padding: 5px 0;
    `,
};
assignBudgetsStyles.shipToAddressSelect = {
    css: css`
        padding-right: 40px !important;
        white-space: nowrap;
        overflow: hidden !important;
        text-overflow: ellipsis;
    `,
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const [offset, setOffset] = useState(0);
    useEffect(() => {
        window.onscroll = () => {
            const check = window.parent.document.getElementById("siteIFrame");
            if (!check) {
                setOffset(window.pageYOffset);
            } else {
                setOffset(2000);
            }
        };
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
            <Page
                as="header"
                isHeader={true}
                fullWidth={[true, true, true, true, true]}
                style={{ position: "fixed" }}
                {...(offset > 130 ? { ...styles.headerMainFixed } : { ...styles.headerMain })}
            >
                <StyledWrapper {...styles.headerMainWrapper}>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <Zone contentId={id} zoneName="SecondaryNavigation" />
                    <Zone contentId={id} zoneName="Breadcrumb" />
                    <span ref={afterHeader} tabIndex={-1} />
                </StyledWrapper>
                <KeepMeLoginModal/>
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
